import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { notification, Button, Space, Typography, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import usePaymentTransactionOrder from "../../hooks/cases/usePaymentTransactionOrder";
import useCaseTransactionStatus from "../../hooks/cases/useCaseTransactionStatus";
import useInvoiceTransactionStatus from "../../hooks/cases/useInvoiceTransactionStatus";
import apiService from "../../axios/apiService";
import { InvoiceType, PaymentFor } from "../../appContants";
const { Text } = Typography;
const PaypalPaymentButtonSubmitCase = ({
  handleSubmit,
  activeTab,
  paymentFor,
  onSubmitCaseLoadingChange,
  parentBusy,
}) => {
  const caseIdRef = useRef(null); // <<-- realtime safe storage
  const [api, contextHolder] = notification.useNotification();
  const [{ isPending }] = usePayPalScriptReducer();

  const { createOrder, completeOrder, cancelOrder } = usePaymentTransactionOrder();
  const { getData: getCaseData } =
    useCaseTransactionStatus();
  const { getData: getInvoiceData } =
    useInvoiceTransactionStatus();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /**
   * Step 1 — SAVE CASE BEFORE PAYMENT
   */
  const checkPaymentStatus = async () => {
      let response;
      if (paymentFor === PaymentFor.CASE) {
        response = await getCaseData(caseIdRef.current);
      } else {
        response = await getInvoiceData(caseIdRef.current);
      }
      
      console.log('✌️response --->', response);
      if (paymentFor === PaymentFor.CASE) {
        const notificationObj = {
          message: 'Payment Pending',
          description: (
            <Space direction="vertical">
              <Text>
                You have invoices with pending payments. Please check your
                invoices and make the payments accordingly. Only then can you
                submit any new cases.
              </Text>
              <Button
                // type="primary"
                size="small"
                onClick={() => navigate('/user/invoicelist')}
              >
                View Invoices
              </Button>
            </Space>
          ),
        };
        const isMonthly = response?.paymentMethod === InvoiceType.MONTHLY;
        const isPayAsYouGo =
          response?.paymentMethod === InvoiceType.PAY_AS_YOU_GO;
        if (isMonthly) {
          if (response?.hasPendingMonthlyInvoices) {
            return api.error(notificationObj);
          } else {
            // can submit case if there are no pending invoice payments, as it falls under the MONTHLY payment method
            navigate('/user/case/submitted')
          }
        } else if (isPayAsYouGo) {
          if (response?.hasPendingMonthlyInvoices) {
            return api.error(notificationObj);
          }
        }
      }
      // Check if payment is already done
      if (response?.hasCompletedTransactions) {
        return api.error({ message: 'Payment already done!' });
        // if there is any active payment
      } else if (response?.hasActiveTransactions) {
        return api.error({
          message: 'There are active payments in progress for this record!',
        });
      } else {
        return true; // proceed with payment
      }
    };
  const saveCaseBeforePayment = async () => {
    try {
      const response = await handleSubmit(activeTab, true); // true = save only, do not proceed further

      const newCaseId = response?.caseId || response?.data?.caseId;

      if (!newCaseId) {
        throw new Error("Case ID not returned from backend");
      }

      caseIdRef.current = newCaseId; // <-- safe to assign here

      return newCaseId;
    } catch (error) {

      api.error({
        message: "Case Save Failed",
        description: "Unable to save the case.",
      });

      throw error;
    }
  };
  const handleButtonClick = async () => {
    try {
      setLoading(true);
      onSubmitCaseLoadingChange?.(true);
      let validCaseId = caseIdRef.current;

      // If case was not saved yet, save it now
      if (!validCaseId) {
        validCaseId = await saveCaseBeforePayment();
      }

      const isAllowed = await checkPaymentStatus();
      if (isAllowed !== true) {
        onSubmitCaseLoadingChange?.(false);
        return;
      }

      // Open PayPal modal
      setOpen(true);
    } catch (error) {
      // Errors are already handled in respective functions
      onSubmitCaseLoadingChange?.(false);
    } finally {
      setLoading(false);
    }
  };
  const closeModal = () => {
    onSubmitCaseLoadingChange?.(false);
    setOpen(false);
  };
  /**
   * Step 2 — CREATE PAYPAL ORDER
   */
  const handleCreateOrder = async () => {
    try {
      let validCaseId = caseIdRef.current;

      // // If case was not saved yet, save it now
      // if (!validCaseId) {
      //   validCaseId = await saveCaseBeforePayment();
      // }
     
      // const isAllowed = await checkPaymentStatus();
      // if (isAllowed !== true) {
      //   return;
      // }

      const order = await createOrder(validCaseId, paymentFor);

      if (!order?.orderNo) {
        throw new Error("Failed to create PayPal order. Missing orderNo.");
      }

      return order.orderNo;
    } catch (error) {
      api.error({
        message: "Payment Error",
        description: error?.response?.data?.error || error?.message,
      });
      closeModal();
      throw error;
    }
  };

  /**
   * Step 3 — ON APPROVE (CAPTURE PAYMENT)
   */
  const handleApprove = async (data) => {
    try {
      const orderId = data?.orderID;

      await completeOrder(orderId);

      const submittedCaseId = caseIdRef.current;

      if (paymentFor === PaymentFor.CASE && submittedCaseId) {
        const response = await apiService.markCaseAsSubmitted(submittedCaseId);

        api.success({
          message: "Payment Successful",
          description:
            response?.message ||
            "Your case and payment were processed successfully.",
        });
      } else {
        api.success({
          message: "Payment Successful",
          description: "Your payment was processed successfully.",
        });
      }

      navigate("/user/case/submitted");
    } catch (error) {
      api.error({
        message: "Payment Processing Failed",
        description:
          error?.response?.data?.error ||
          error?.message ||
          "Payment could not be confirmed.",
      });
    } finally {
      closeModal();
    }
  };

  /**
   * Step 4 — PAYMENT CANCEL
   */
  const handleCancel = async (data) => {
    const orderId = data?.orderID;

    api.error({ message: "Payment Cancelled" });

    try {
      await cancelOrder(orderId);
      closeModal();
    } catch (err) {
      console.error("Cancel error:", err);
    }
  };

  return (
    <>
      {contextHolder}

      <Button
              type="primary"
              htmlType="button"
              onClick={handleButtonClick}
              loading={loading}
              disabled={parentBusy}
            >
             Submit Case
            </Button>

      <Modal
        title="Make Payment"
        open={open}
        onCancel={() => setOpen(false)}
        footer={false}
      >
        <Space direction="vertical" size="middle">
            <Text type="secondary">
              {paymentFor === PaymentFor.CASE
                ? 'Please make payment using the button below, in order to submit the case!'
                : 'Please make payment using the button below!'}
            </Text>
        </Space>
        {isPending ? (
          <div>Loading Payment…</div>
        ) : (
          <PayPalButtons
            style={{ layout: "vertical" }}
            createOrder={handleCreateOrder}
            onApprove={handleApprove}
            onCancel={handleCancel}
            onError={(err) => {
              console.log('✌️err --->', err);
              api.error({
                message: "Payment Error",
                description: "Something went wrong with PayPal.",
              });
              closeModal();
            }}
          />
        )}
      </Modal>
    </>
  );
};

PaypalPaymentButtonSubmitCase.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  activeTab: PropTypes.any,
  paymentFor: PropTypes.string.isRequired,
  onSubmitCaseLoadingChange: PropTypes.func,
  parentBusy: PropTypes.bool,
};

export default PaypalPaymentButtonSubmitCase;
